import React, { useState, useEffect } from "react";
import {
  CheckCircle,
  Clock,
  AlertTriangle,
  Target,
  Loader2,
  Shield,
  Users,
  FileText,
  Vote,
  Award,
  Calendar,
  Info,
  ArrowRight,
} from "lucide-react";
import {
  calcularDiasRestantes,
  formatarData,
  formatarDataSimples,
} from "../utils/dateUtils";
import { useCronograma } from "../hooks/useCronograma";
import { useConformidade } from "../hooks/useConformidade";
import { useAuth } from "../context/AuthContext";
import { getSistemaInfo } from "../utils/sistemaInfo";
import { useMandatoAtivo } from "../hooks/useMandatoAtivo";
import { useFlowContext } from "../hooks/useFlowContext";
import { canUseNavigationCTA } from "../utils/flowGates";
import {
  useTabNavigationOptional,
  TabId,
} from "../context/TabNavigationContext";

const DashboardTab: React.FC = () => {
  const { fases, loading, error, getEstatisticas } = useCronograma();
  const { empresaSelecionada } = useAuth();

  // IMPORTANTE: Usar o mandato ativo da tabela cp_cipa_mandato, não campos legados
  const {
    periodoFormatado,
    temMandatoAtivo,
    loading: loadingMandato,
  } = useMandatoAtivo();

  // Hook para validar fluxo de etapas (travas de ordem)
  const {
    canAccessStep,
    nextStep,
    context,
    loading: loadingFlow,
  } = useFlowContext();

  // Hook para navegação entre abas (opcional - retorna null se não estiver no contexto)
  const tabNavigation = useTabNavigationOptional();

  const [showAnalysis, setShowAnalysis] = useState(false);

  // Obter informações dinâmicas do sistema baseado no tipo de comissão
  const cnaeEmpresa = (empresaSelecionada as any)?.cnae_principal;
  const tipoComissaoBanco = (empresaSelecionada as any)?.tipo_comissao;
  const nomeEmpresa = (empresaSelecionada as any)?.nome;

  const [sistemaInfo, setSistemaInfo] = useState({
    nome: "CIPA FÁCIL",
    nomeCompleto: "CIPA FÁCIL EHS",
    descricao: "Sistema Completo de Gestão CIPA - 100% Conforme NR-05",
    descricaoCurta: "Sistema 100% Conforme NR-05 Vigente",
    tipo: "CIPA" as "CIPA" | "CIPAMIN" | "CIPATR",
    norma: "NR-05",
  });

  useEffect(() => {
    getSistemaInfo(cnaeEmpresa, tipoComissaoBanco, nomeEmpresa).then(
      setSistemaInfo,
    );
  }, [cnaeEmpresa, tipoComissaoBanco, nomeEmpresa]);

  // Função para obter o ícone correto
  const getIconComponent = (icon: any) => {
    if (typeof icon === "string") {
      switch (icon) {
        case "Users":
          return Users;
        case "FileText":
          return FileText;
        case "Vote":
          return Vote;
        case "CheckCircle":
          return CheckCircle;
        case "Award":
          return Award;
        case "Calendar":
          return Calendar;
        default:
          return Calendar;
      }
    }
    return icon || Calendar;
  };

  // Hook de conformidade dinâmica
  const {
    analiseConformidade,
    resumo: resumoConformidade,
    loading: loadingConformidade,
    getStatusColor,
    getStatusLabel,
    itensCriticos,
  } = useConformidade();

  // Mapear nomes de etapas para exibição amigável
  const stepLabels: Record<string, string> = {
    empresa: "Cadastrar Empresa",
    mandato: "Configurar Mandato",
    membros: "Cadastrar Membros",
    cronograma: "Criar Cronograma",
    reunioes: "Agendar Reuniões",
    ata: "Gerar Ata Digital",
    "plano-trabalho": "Plano de Trabalho",
    relatorios: "Relatórios",
    documentos: "Documentos",
    export: "Exportar",
  };

  // Verificar próxima etapa recomendada
  const getNextStepInfo = () => {
    if (!nextStep) return null;

    // Verificar se pode acessar o cronograma (step intermediário importante)
    const cronogramaAccess = canAccessStep("cronograma");
    if (!cronogramaAccess.allowed) {
      return {
        // ctaTab já é um TabId válido (ex: "gestao-mandatos"), não um FlowStep
        tabId: cronogramaAccess.ctaTab || "gestao-mandatos",
        label: cronogramaAccess.ctaLabel || "Configurar Mandato",
        reason: cronogramaAccess.reason || "Complete a etapa anterior",
      };
    }

    return {
      // nextStep é um FlowStep, precisa converter para TabId
      tabId: null, // Indica que precisa usar navigateToFlowStep
      flowStep: nextStep,
      label: stepLabels[nextStep] || "Próxima Etapa",
      reason: `Complete esta etapa para avançar no processo eleitoral`,
    };
  };

  // Verificar se o plano atual permite usar o CTA de navegação
  // Apenas ENTERPRISE e SUPER_ADMIN têm acesso completo a todas as tabs
  const podeUsarCTA = canUseNavigationCTA(context.planoUsuario);
  const nextStepInfo = podeUsarCTA ? getNextStepInfo() : null;

  if (loading || loadingConformidade || loadingMandato || loadingFlow) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Carregando dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 m-6">
        <p className="text-red-700">Erro ao carregar dados: {error}</p>
      </div>
    );
  }

  const stats = getEstatisticas();

  return (
    <div className="space-y-6">
      <div
        className={`bg-gradient-to-r ${
          sistemaInfo.tipo === "CIPAMIN"
            ? "from-orange-600 to-red-600"
            : "from-blue-600 to-purple-600"
        } text-white rounded-xl p-6`}
      >
        <h1 className="text-3xl font-bold mb-2">{sistemaInfo.nome}</h1>
        <p
          className={`${
            sistemaInfo.tipo === "CIPAMIN" ? "text-orange-100" : "text-blue-100"
          }`}
        >
          {sistemaInfo.descricao}
        </p>
        <div className="mt-4 p-3 bg-white bg-opacity-20 rounded-lg">
          <p className="text-sm">
            <strong>Mandato Atual:</strong>{" "}
            {temMandatoAtivo ? periodoFormatado : "Nenhum mandato configurado"}
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg p-4 shadow-lg border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Fases Concluídas</p>
              <p className="text-2xl font-bold text-green-600">
                {stats.concluidas}
              </p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 shadow-lg border-l-4 border-orange-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Próximo Vencimento</p>
              <p className="text-2xl font-bold text-orange-600">
                {stats.proximoVencimento}
              </p>
            </div>
            <Clock className="w-8 h-8 text-orange-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 shadow-lg border-l-4 border-red-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Fases Vencidas</p>
              <p className="text-2xl font-bold text-red-600">
                {stats.vencidas}
              </p>
            </div>
            <AlertTriangle className="w-8 h-8 text-red-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 shadow-lg border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total de Fases</p>
              <p className="text-2xl font-bold text-blue-600">{stats.total}</p>
            </div>
            <Target className="w-8 h-8 text-blue-500" />
          </div>
        </div>
      </div>

      {/* INFORMAÇÕES DO MANDATO (Somente leitura) */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
          <Info className="w-5 h-5 text-blue-600" />
          Informações do Mandato
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block font-medium text-gray-700 mb-2">
              Período do Mandato Ativo
            </label>
            <div
              className={`w-full px-3 py-2 border rounded-lg font-medium ${
                temMandatoAtivo
                  ? "border-green-200 bg-green-50 text-green-700"
                  : "border-orange-200 bg-orange-50 text-orange-700"
              }`}
            >
              {periodoFormatado}
            </div>
            <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
              <Info className="w-3 h-3" />
              Para gerenciar mandatos, acesse a aba <strong>Mandato</strong>
            </p>
          </div>
          <div className="flex p-8 m-auto gap-2">
            <button
              onClick={() => setShowAnalysis(!showAnalysis)}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
            >
              {showAnalysis ? "Ocultar Análise" : "Análise de Conformidade"}
            </button>
          </div>
        </div>
      </div>

      {/* CTA DINÂMICO - Próxima etapa recomendada 
          Visível apenas para ENTERPRISE e SUPER_ADMIN.
          STARTER e PROFESSIONAL não têm acesso a todas as tabs,
          portanto o CTA é ocultado para evitar navegação para áreas restritas. */}
      {nextStepInfo && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-start gap-3">
              <div className="bg-blue-100 p-2 rounded-lg">
                <ArrowRight className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-blue-800 mb-1">
                  Próxima Etapa Recomendada
                </h3>
                <p className="text-sm text-blue-700">{nextStepInfo.reason}</p>
              </div>
            </div>
            <button
              onClick={() => {
                // Usar o contexto de navegação se disponível
                if (tabNavigation && nextStepInfo) {
                  // Se temos tabId direto (do ctaTab), usar navigateTo
                  // Se temos flowStep, usar navigateToFlowStep para converter
                  if (nextStepInfo.tabId) {
                    tabNavigation.navigateTo(nextStepInfo.tabId as TabId);
                  } else if (nextStepInfo.flowStep) {
                    tabNavigation.navigateToFlowStep(nextStepInfo.flowStep);
                  }
                }
              }}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 font-medium shadow-md"
            >
              {nextStepInfo.label}
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* ANÁLISE DE CONFORMIDADE - Só exibe se houver mandato ativo */}
      {showAnalysis && temMandatoAtivo && (
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
            <Shield
              className={`w-5 h-5 ${resumoConformidade.alertaCritico ? "text-red-600" : "text-green-600"}`}
            />
            Análise de Conformidade NR-05
          </h3>

          {/* Resumo de Conformidade */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
            <div className="bg-green-50 p-3 rounded-lg text-center">
              <p className="text-2xl font-bold text-green-600">
                {resumoConformidade.conformes}
              </p>
              <p className="text-xs text-green-700">Conformes</p>
            </div>
            <div className="bg-red-50 p-3 rounded-lg text-center">
              <p className="text-2xl font-bold text-red-600">
                {resumoConformidade.atrasados}
              </p>
              <p className="text-xs text-red-700">Atrasados</p>
            </div>
            <div className="bg-yellow-50 p-3 rounded-lg text-center">
              <p className="text-2xl font-bold text-yellow-600">
                {resumoConformidade.emAndamento}
              </p>
              <p className="text-xs text-yellow-700">Em Andamento</p>
            </div>
            <div className="bg-gray-50 p-3 rounded-lg text-center">
              <p className="text-2xl font-bold text-gray-600">
                {resumoConformidade.pendentes}
              </p>
              <p className="text-xs text-gray-700">Pendentes</p>
            </div>
            <div
              className={`p-3 rounded-lg text-center ${resumoConformidade.percentualConformidade >= 80 ? "bg-green-50" : "bg-red-50"}`}
            >
              <p
                className={`text-2xl font-bold ${resumoConformidade.percentualConformidade >= 80 ? "text-green-600" : "text-red-600"}`}
              >
                {resumoConformidade.percentualConformidade}%
              </p>
              <p
                className={`text-xs ${resumoConformidade.percentualConformidade >= 80 ? "text-green-700" : "text-red-700"}`}
              >
                Conformidade
              </p>
            </div>
          </div>

          {/* Alerta de itens críticos */}
          {itensCriticos.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                <span className="font-bold text-red-800">
                  Atenção: {itensCriticos.length} item(ns) requer(em) ação
                  imediata!
                </span>
              </div>
              <ul className="list-disc pl-5 text-sm text-red-700">
                {itensCriticos.slice(0, 3).map((item) => (
                  <li key={item.id}>
                    {item.item} - {item.implementado}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2">Item</th>
                  <th className="text-left py-2">Exigência NR-05</th>
                  <th className="text-left py-2">Situação</th>
                  <th className="text-left py-2">Status</th>
                  <th className="text-left py-2">Artigo</th>
                </tr>
              </thead>
              <tbody>
                {analiseConformidade.map((item) => (
                  <tr key={item.id} className="border-b">
                    <td className="py-2 font-medium">{item.item}</td>
                    <td className="py-2 text-gray-600">{item.exigencia}</td>
                    <td
                      className={`py-2 font-medium ${
                        item.status === "conforme"
                          ? "text-green-600"
                          : item.status === "atrasado" ||
                              item.status === "nao_conforme"
                            ? "text-red-600"
                            : item.status === "em_andamento"
                              ? "text-yellow-600"
                              : "text-gray-600"
                      }`}
                    >
                      {item.implementado}
                    </td>
                    <td className="py-2">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(item.status)}`}
                      >
                        {getStatusLabel(item.status)}
                      </span>
                    </td>
                    <td className="py-2 text-blue-600 text-sm">
                      {item.artigo}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Aviso quando não há mandato e usuário tentou ver análise */}
      {showAnalysis && !temMandatoAtivo && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-orange-500 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-lg font-bold text-orange-800 mb-2">
                Análise de Conformidade Indisponível
              </h3>
              <p className="text-sm text-orange-700 mb-3">
                A análise de conformidade NR-05 requer um mandato ativo
                configurado com cronograma para calcular prazos e status das
                fases.
              </p>
              <p className="text-sm text-orange-600">
                👉 Acesse a aba <strong>Mandato</strong> para criar ou ativar um
                mandato.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* FASES VENCIDAS - Só exibe se houver mandato ativo */}
      {temMandatoAtivo && (
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            Fases Vencidas
          </h3>
          {stats.fasesVencidas && stats.fasesVencidas.length > 0 ? (
            <ul className="list-disc pl-5 space-y-2">
              {stats.fasesVencidas.map((fase: any, index: number) => (
                <li key={index} className="text-sm text-gray-700">
                  {fase.titulo} - {formatarData(fase.data)}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-500 italic">
              Nenhuma fase vencida. Parabéns! 🎉
            </p>
          )}
        </div>
      )}

      {/* PRÓXIMAS AO VENCIMENTO - Só exibe se houver mandato ativo */}
      {temMandatoAtivo && (
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-orange-600" />
            Próximas ao Vencimento
          </h3>
          {fases.filter((fase) => {
            const diasRestantes = calcularDiasRestantes(fase.data);
            return diasRestantes > 0 && diasRestantes <= 7;
          }).length > 0 ? (
            <ul className="list-disc pl-5 space-y-2">
              {fases
                .filter((fase) => {
                  const diasRestantes = calcularDiasRestantes(fase.data);
                  return diasRestantes > 0 && diasRestantes <= 7;
                })
                .map((fase: any, index: number) => (
                  <li key={index} className="text-sm text-gray-700">
                    {fase.titulo} - {formatarData(fase.data)}
                  </li>
                ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-500 italic">
              Nenhuma fase próxima ao vencimento nos próximos 7 dias.
            </p>
          )}
        </div>
      )}

      {/* PRÓXIMAS AÇÕES CRÍTICAS - Só exibe se houver mandato ativo */}
      {temMandatoAtivo && fases.length > 0 && (
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-orange-600" />
            Próximas Ações Críticas
          </h3>
          <div className="space-y-3">
            {fases.slice(0, 3).map((fase) => {
              const diasRestantes = calcularDiasRestantes(fase.data);
              const IconComponent = getIconComponent(fase.icon);
              return (
                <div
                  key={fase.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className={`${fase.cor} p-2 rounded-lg`}>
                      <IconComponent className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <p className="font-medium">{fase.titulo}</p>
                      <p className="text-sm text-gray-600">
                        {formatarDataSimples(fase.data)}
                      </p>
                    </div>
                  </div>
                  <span
                    className={`px-3 py-1 rounded-full text-sm font-medium ${
                      diasRestantes < 0
                        ? "bg-red-100 text-red-800"
                        : diasRestantes <= 7
                          ? "bg-orange-100 text-orange-800"
                          : "bg-green-100 text-green-800"
                    }`}
                  >
                    {diasRestantes > 0 ? `${diasRestantes} dias` : "Vencido"}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* AVISO QUANDO NÃO HÁ MANDATO ATIVO */}
      {!temMandatoAtivo && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-orange-500 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-lg font-bold text-orange-800 mb-2">
                Nenhum Mandato Ativo Configurado
              </h3>
              <p className="text-sm text-orange-700 mb-3">
                Para visualizar as fases do cronograma, prazos e ações críticas,
                é necessário configurar um mandato ativo para esta empresa.
              </p>
              <p className="text-sm text-orange-600">
                👉 Acesse a aba <strong>Mandato</strong> para criar ou ativar um
                mandato.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardTab;
